import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Building2, MapPinned, ClipboardCheck, LogOut, Menu, X, QrCode, ExternalLink, Settings2, Radar, Camera, CalendarClock, Siren, FolderLock, FileBarChart } from "lucide-react";
import { OpsProvider, useOps } from "@/ops/OpsContext";
import { OpsLogin, ChangePassword } from "@/ops/OpsLogin";
import { Loading } from "@/ops/ui";
import { Seo } from "@/components/site/Seo";
import OpsDashboard from "@/ops/admin/OpsDashboard";
import Employees from "@/ops/admin/Employees";
import Clients from "@/ops/admin/Clients";
import Projects from "@/ops/admin/Projects";
import ProjectDetail from "@/ops/admin/ProjectDetail";
import QrPrint from "@/ops/admin/QrPrint";
import PatrolReport from "@/ops/admin/PatrolReport";
import StaffHome from "@/ops/staff/StaffHome";
import Scan from "@/ops/staff/Scan";
import ClientHome from "@/ops/client/ClientHome";
import GpsSettings from "@/ops/admin/GpsSettings";
import Live from "@/ops/admin/Live";
import PhotoPatrol from "@/ops/admin/PhotoPatrol";
import Shifts from "@/ops/admin/Shifts";
import Incidents from "@/ops/admin/Incidents";
import PersonnelDocs from "@/ops/admin/PersonnelDocs";
import Reports from "@/ops/admin/Reports";

const NAV = {
  admin: [["/operasyon", "Yönetim Paneli", LayoutDashboard, true], ["/operasyon/canli", "Canlı Takip ve Geçmiş", Radar],
    ["/operasyon/devriye", "Devriye Raporu", ClipboardCheck], ["/operasyon/fotografli", "Fotoğraflı Devriye", Camera],
    ["/operasyon/projeler", "Projeler ve Noktalar", MapPinned], ["/operasyon/gps", "GPS ve Devriye Ayarları", Settings2],
    ["/operasyon/personel", "Personel", Users], ["/operasyon/vardiya", "Vardiya ve Puantaj", CalendarClock],
    ["/operasyon/belgeler", "Özlük Belgeleri", FolderLock], ["/operasyon/olaylar", "Olay Bildirimleri", Siren],
    ["/operasyon/musteriler", "Müşteriler", Building2], ["/operasyon/raporlar", "Raporlar", FileBarChart]],
  staff: [["/operasyon", "Görevlerim", LayoutDashboard, true], ["/operasyon/tara", "QR Okut", QrCode]],
  client: [["/operasyon", "Projelerim", LayoutDashboard, true]],
};
const ROLE = { admin: "Yönetici", staff: "Güvenlik Personeli", client: "Müşteri" };

const navCls = ({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[color:var(--red)] text-white" : "text-zinc-300 hover:bg-white/5 hover:text-white"}`;

function Sidebar({ me, open }) {
  return (
    <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-[color:var(--ink)] transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
      <div className="flex h-16 items-center gap-3 border-b border-white/10 px-5">
        <img src="/assets/logo-shield.webp" alt="" className="h-9" />
        <div><p className="font-display text-sm font-extrabold text-white">Operasyon</p><p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{ROLE[me.role]}</p></div>
      </div>
      <nav className="flex max-h-[calc(100vh-64px)] flex-col gap-0.5 overflow-y-auto p-3">
        {(NAV[me.role] || []).map(([to, label, Icon, end]) => (
          <NavLink key={to} to={to} end={end} className={navCls} data-testid={`ops-nav-${to.split("/")[2] || "home"}`}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
        {me.role === "admin" && <a href="/admin" className="mt-4 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-zinc-400 hover:text-white" data-testid="ops-nav-admin-panel"><ExternalLink className="h-4 w-4" /> Web sitesi yönetimi</a>}
      </nav>
    </aside>
  );
}

function Topbar({ me, open, toggle, logout }) {
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-zinc-200 bg-white px-4 sm:px-6">
      <button className="lg:hidden" onClick={toggle} aria-label="Menü" data-testid="ops-menu-toggle">{open ? <X /> : <Menu />}</button>
      <span className="truncate text-sm text-zinc-500"><b className="text-zinc-900" data-testid="ops-user-name">{me.name}</b> · {ROLE[me.role]}</span>
      <button onClick={logout} className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100" data-testid="ops-logout-btn"><LogOut className="h-4 w-4" /> Çıkış</button>
    </header>
  );
}

function gateTarget(me, loc) {
  if (me.must_change) {
    sessionStorage.setItem("ops_next", loc.pathname + loc.search);
    return "/operasyon/sifre";
  }
  return null;
}

function Layout() {
  const { me, logout, loggedOut } = useOps();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  if (me === null) return <Loading />;
  if (!me) {
    if (!loggedOut) sessionStorage.setItem("ops_next", loc.pathname + loc.search);
    return <Navigate to="/operasyon/giris" replace />;
  }
  const target = gateTarget(me, loc);
  if (target) return <Navigate to={target} replace />;
  return (
    <div className="min-h-screen bg-zinc-100">
      <Seo title="Operasyon | Rüzgâr Özel Güvenlik" noindex path="/operasyon" />
      <Sidebar me={me} open={open} />
      <div className="lg:pl-64">
        <Topbar me={me} open={open} toggle={() => setOpen(!open)} logout={logout} />
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}

function Home() {
  const { me } = useOps();
  if (me.role === "admin") return <OpsDashboard />;
  if (me.role === "staff") return <StaffHome />;
  return <ClientHome />;
}

function AdminOnly({ children }) {
  const { me } = useOps();
  if (me === null) return <Loading />;
  if (!me) return <Navigate to="/operasyon/giris" replace />;
  return me.role === "admin" ? children : <Navigate to="/operasyon" replace />;
}

function PwGate() {
  const { me } = useOps();
  if (me === null) return <Loading />;
  if (!me) return <Navigate to="/operasyon/giris" replace />;
  return <ChangePassword />;
}

export default function OpsApp() {
  return (
    <OpsProvider>
      <Routes>
        <Route path="giris" element={<OpsLogin />} />
        <Route path="sifre" element={<PwGate />} />
        <Route path="projeler/:id/qr" element={<AdminOnly><QrPrint /></AdminOnly>} />
        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="tara" element={<Scan />} />
          <Route path="personel" element={<AdminOnly><Employees /></AdminOnly>} />
          <Route path="musteriler" element={<AdminOnly><Clients /></AdminOnly>} />
          <Route path="projeler" element={<AdminOnly><Projects /></AdminOnly>} />
          <Route path="projeler/:id" element={<AdminOnly><ProjectDetail /></AdminOnly>} />
          <Route path="devriye" element={<AdminOnly><PatrolReport /></AdminOnly>} />
          <Route path="canli" element={<AdminOnly><Live /></AdminOnly>} />
          <Route path="fotografli" element={<AdminOnly><PhotoPatrol /></AdminOnly>} />
          <Route path="gps" element={<AdminOnly><GpsSettings /></AdminOnly>} />
          <Route path="vardiya" element={<AdminOnly><Shifts /></AdminOnly>} />
          <Route path="belgeler" element={<AdminOnly><PersonnelDocs /></AdminOnly>} />
          <Route path="olaylar" element={<AdminOnly><Incidents /></AdminOnly>} />
          <Route path="raporlar" element={<AdminOnly><Reports /></AdminOnly>} />
          <Route path="*" element={<Navigate to="/operasyon" replace />} />
        </Route>
      </Routes>
    </OpsProvider>
  );
}
